import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { useSidebarLayout } from "../../../contexts/SidebarLayoutContext";
import { getActiveSidebarPathOverride } from "../../../utils/navigationContext";

const getSidebarItemClass = (isActive, extra = "") =>
  [
    "group flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-sf-medium leading-5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2",
    isActive
      ? "bg-[#F1F6FF] font-sf-semibold text-[#0155F8]"
      : "text-[#30394A] hover:bg-[#F6FAFF] hover:text-[#0155F8]",
    extra,
  ]
    .filter(Boolean)
    .join(" ");

const getSidebarSubItemClass = (isActive) =>
  [
    "block truncate rounded-lg px-3 py-2 text-sm font-sf-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2",
    isActive
      ? "bg-[#F1F6FF] font-sf-semibold text-[#0155F8]"
      : "text-[#3F4A5F] hover:bg-[#F6FAFF] hover:text-[#0155F8]",
  ].join(" ");
const Sidebar = () => {
  const { isSidebarOpen, toggleSidebar, navLinks } = useSidebarLayout();

  const location = useLocation();
  const activeSidebarPathOverride = getActiveSidebarPathOverride(location);
  const [openMenus, setOpenMenus] = useState({});
  const submenuRefs = useRef({});
  const [floatingSubmenu, setFloatingSubmenu] = useState(null);

  const checkIsActive = useCallback(
    (path) =>
      activeSidebarPathOverride === path ||
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`),
    [activeSidebarPathOverride, location.pathname]
  );

  const toggleMenu = (index) => {
    setOpenMenus((prev) => {
      const next = { ...prev, [index]: !prev[index] };
      if (!isSidebarOpen && !prev[index]) {
        const rect = submenuRefs.current[index]?.getBoundingClientRect();
        if (rect) {
          setFloatingSubmenu({
            top: rect.top,
            left: rect.right + 8,
            items: navLinks[index].subLinks,
            activeTo: activeSidebarPathOverride || location.pathname,
          });
        }
      } else {
        setFloatingSubmenu(null);
      }
      return next;
    });
  };

  useEffect(() => {
    const init = {};
    navLinks?.forEach((link, i) => {
      if (link.subLinks?.some((sub) => checkIsActive(sub.to))) init[i] = true;
    });
    setOpenMenus(init);
  }, [location.pathname, checkIsActive]);

  return (
    <>
      <aside
        className={`${isSidebarOpen ? "w-[280px]" : "w-[86px]"
          } h-full overflow-hidden shrink-0 transition-[width] duration-300 ease-in-out`}
        style={{ willChange: "width" }}
      >
        <div className="flex h-full w-full flex-col justify-between bg-white p-4 border-r">
          <nav className="h-full overflow-y-auto py-4">
            <ul className="space-y-1">
              {navLinks?.map((link, index) => {
                const hasActiveSubLink = link.subLinks?.some((sub) =>
                  checkIsActive(sub.to),
                );
                const isActive = checkIsActive(link.to) || hasActiveSubLink;
                const isSubOpen = openMenus[index];

                const ItemLeft = (
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="relative flex size-7 shrink-0 items-center justify-center text-current">
                      {link.icon}

                      {!isSidebarOpen && link.badge > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-[4px] rounded-full bg-orange-500 text-white text-[10px] font-semibold flex items-center justify-center">
                          {link.badge > 99 ? "99+" : link.badge}
                        </span>
                      )}
                    </span>

                    <span
                      className={[
                        "flex items-center gap-2 truncate transition-[max-width,opacity] duration-300 ease-in-out",
                        isSidebarOpen
                          ? "max-w-[180px] opacity-100"
                          : "max-w-0 opacity-0",
                      ].join(" ")}
                      title={link.label}
                    >
                      {link.label}

                      {isSidebarOpen && link.badge > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-semibold">
                          {link.badge > 99 ? "99+" : link.badge}
                        </span>
                      )}
                    </span>
                  </div>
                );

                if (link.subLinks) {
                  return (
                    <li key={index} className="relative">
                      <button
                        ref={(el) => (submenuRefs.current[index] = el)}
                        onClick={() => toggleMenu(index)}
                        className={getSidebarItemClass(
                          isActive,
                          "justify-between",
                        )}
                        aria-expanded={isSubOpen}
                      >
                        {ItemLeft}

                        <div
                          className={[
                            "ml-3 h-4 flex-shrink-0 overflow-hidden",
                            "transition-[width,opacity] ease-linear",
                            isSidebarOpen
                              ? "w-4 opacity-100 delay-[100ms] duration-0"
                              : "w-0 opacity-0 delay-[0ms] duration-0 pointer-events-none",
                          ].join(" ")}
                          aria-hidden={!isSidebarOpen}
                          style={{ willChange: "width, opacity" }}
                        >
                          <ArrowDownIcon
                            className={[
                              "h-4 w-4",
                              "transition-transform duration-300 ease-linear",
                              isSubOpen ? "rotate-180" : "rotate-0",
                            ].join(" ")}
                            style={{ willChange: "transform" }}
                          />
                        </div>
                      </button>

                      <div
                        className={[
                          "pl-8",
                          isSidebarOpen
                            ? isSubOpen
                              ? "grid grid-rows-[1fr] mt-1"
                              : "grid grid-rows-[0fr] mt-0"
                            : "hidden",
                          "transition-[grid-template-rows,margin] duration-300 ease-in-out",
                        ].join(" ")}
                      >
                        <ul className="space-y-1 overflow-hidden">
                          {link.subLinks?.map((sub, idx) => (
                            <li key={idx}>
                              <Link
                                to={sub.to}
                                className={getSidebarSubItemClass(
                                  (activeSidebarPathOverride || location.pathname) === sub.to,
                                )}
                                title={sub.label}
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index}>
                    <Link
                      to={link.to}
                      className={getSidebarItemClass(isActive)}
                      title={link.label}
                    >
                      {ItemLeft}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <button
            className="mt-4 flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-sf-medium text-[#30394A] transition-colors duration-150 hover:bg-[#F6FAFF] hover:text-[#0155F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2"
            onClick={toggleSidebar}
            title={isSidebarOpen ? "Thu gọn" : "Mở rộng"}
          >
            <img
              src={
                isSidebarOpen ? "/icons/close-menu.svg" : "/icons/open-menu.svg"
              }
              alt="toggle"
              className="size-3.5 shrink-0"
            />
            <span
              className={[
                "truncate transition-[max-width,opacity] duration-300 ease-in-out",
                isSidebarOpen
                  ? "max-w-[120px] opacity-100"
                  : "max-w-0 opacity-0",
              ].join(" ")}
            >
              Thu gọn
            </span>
          </button>
        </div>
      </aside>

      {!isSidebarOpen && floatingSubmenu && (
        <ul
          className="fixed z-[9999] min-w-[200px] rounded-xl border border-slate-100 bg-white p-2 shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
          style={{ top: floatingSubmenu.top, left: floatingSubmenu.left }}
        >
          {floatingSubmenu?.items?.map((sub, idx) => (
            <li key={idx}>
              <Link
                to={sub.to}
                className={getSidebarSubItemClass(
                  floatingSubmenu.activeTo === sub.to,
                )}
                title={sub.label}
              >
                {sub.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default Sidebar;
